import { FullStory, init, isInitialized } from "@fullstory/browser";
import { isEmbedMode } from "../../../utils/embedMode";
import type { ConsentObj } from "../../../app/pages/invoices/consent/interface";

type Props = Record<string, unknown>;

const INVALID_PROPERTY_CHARS = /[^A-Za-z0-9_-]/g;
const STARTS_WITH_LETTER = /^[A-Za-z]/;

function toFullStoryPropertyName(key: string) {
  const sanitized = key.replace(INVALID_PROPERTY_CHARS, "_");
  return STARTS_WITH_LETTER.test(sanitized) ? sanitized : `p${sanitized}`;
}

function cleanProps(props?: Props): Props {
  const out: Props = {};
  if (!props) return out;
  for (const [key, value] of Object.entries(props)) {
    if (value !== undefined && value !== null && value !== "") {
      out[toFullStoryPropertyName(key)] = value;
    }
  }
  return out;
}

function send(...args: Parameters<typeof FullStory>) {
  try {
    if (!isInitialized()) return;
    FullStory(...args);
  } catch {
    // never break UX for analytics
  }
}

export function initAnalytics({ orgId }: { orgId?: string }) {
  if (!orgId) return;
  try {
    init({ orgId });
  } catch {
    // ignore
  }
}

export function track(event: string, properties?: Props) {
  send("trackEvent", { name: event, properties: cleanProps(properties) });
}

export function identify(userId: string, traits?: Props) {
  const { name, ...rest } = cleanProps(traits);
  const properties: Props = { ...rest };
  if (name !== undefined) properties.displayName = name;
  send(
    "setIdentity",
    Object.keys(properties).length
      ? { uid: userId, properties }
      : { uid: userId }
  );
}

export function page(name?: string, properties?: Props) {
  send("setProperties", {
    type: "page",
    properties: name
      ? { pageName: name, ...cleanProps(properties) }
      : cleanProps(properties),
  });
}

export function reset() {
  send("setIdentity", { anonymous: true });
}

/** Shared funnel context for payer / payment-link dashboards. */
export function buildPayerContext(
  consentForm?: Partial<ConsentObj> | null
): Props {
  const org =
    consentForm?.invoiceConsent?.organization ||
    consentForm?.paymentLink?.organization;
  return cleanProps({
    module: consentForm?.module,
    payment_link_id: consentForm?.paymentLinkId,
    consent_id: consentForm?.consentId,
    payment_id: consentForm?.paymentId || consentForm?.selectedPayment?.id,
    invoice_id: consentForm?.invoiceConsent?.invoice?.id,
    organization_id: org?.id,
    organization_name: org?.name,
    amount:
      consentForm?.totalAmount ??
      consentForm?.intent_total ??
      consentForm?.selectedPayment?.amount,
    payment_option: consentForm?.paymentOption,
    payment_type: consentForm?.paymentType,
    installment_count: consentForm?.installmentCount,
    splitting_period: consentForm?.splittingPeriod,
    payment_method: consentForm?.paymentMethod,
    intent_id: consentForm?.intent_id,
    is_embed: isEmbedMode(),
  });
}

export function trackPayer(
  event: string,
  properties?: Props,
  consentForm?: Partial<ConsentObj> | null
) {
  track(event, {
    ...buildPayerContext(consentForm),
    ...cleanProps(properties),
  });
}
